import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import {
    TrendingDownRounded,
    TrendingFlatRounded,
    TrendingUpRounded,
    MapRounded,
} from "@mui/icons-material";
import { colors } from "../../theme/colors";
import { getMapImageUrl, getMapMetaById } from "../../constants/mapMeta";
import utils from "../../universal/utils";
import { EmptyDataState } from "../common/EmptyDataState";
import {
    buildMapRatingAverages,
    getMapWinTrendDirection,
    getSortedMapStatEntries,
} from "./profileRecentMatches";

const edgeBasePath = utils.getEdgeBasePath();

const ROW_SURFACE_SX = {
    background: `
        linear-gradient(
            180deg,
            rgba(14,14,17,0.98) 0%,
            rgba(9,9,11,0.99) 100%
        )
    `,
    border: "1px solid rgba(255,255,255,0.05)",
    boxShadow: `
        inset 0 1px 0 rgba(255,255,255,0.04),
        inset 0 -24px 48px rgba(246,185,76,0.02)
    `,
};

const STAT_LABEL_SX = {
    fontSize: "0.58rem",
    fontWeight: 700,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: "rgba(255,255,255,0.34)",
    lineHeight: 1,
};

function buildMapRows(mapsStats, mapRatings, limit) {
    return getSortedMapStatEntries(mapsStats, limit).map(([mapId, stats]) => ({
        mapId,
        gamesPlayed: stats.gamesPlayed,
        winRate: stats.winRate ?? 0,
        avgRating: mapRatings[mapId] ?? null,
        recentResults: stats.resultLastFiveGames || [],
        trend: getMapWinTrendDirection(stats.resultLastFiveGames),
    }));
}

const TREND_CONFIG = {
    up: { Icon: TrendingUpRounded, color: colors.state.success.main },
    down: { Icon: TrendingDownRounded, color: colors.state.danger.main },
    neutral: { Icon: TrendingFlatRounded, color: colors.text.muted },
};

function RecentResultsStrip({ results }) {
    if (!results.length) {
        return (
            <Typography sx={{ ...STAT_LABEL_SX, color: "rgba(255,255,255,0.28)" }}>
                —
            </Typography>
        );
    }

    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
            {results.map((result, index) => {
                const isWin = result === "W";
                const badgeColor = isWin
                    ? colors.state.success.main
                    : colors.state.danger.main;

                return (
                    <Box
                        key={`${result}-${index}`}
                        sx={{
                            width: "1rem",
                            height: "1rem",
                            borderRadius: "3px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: badgeColor,
                            flexShrink: 0,
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: "0.56rem",
                                fontWeight: 800,
                                color: colors.text.primary,
                                lineHeight: 1,
                            }}
                        >
                            {result}
                        </Typography>
                    </Box>
                );
            })}
        </Box>
    );
}
function MapPerformanceRow({
    mapId,
    gamesPlayed,
    winRate,
    avgRating,
    recentResults,
    trend,
}) {
    const mapMeta = getMapMetaById(mapId);
    const mapLabel = mapMeta?.display || mapId;
    const mapImageUrl = getMapImageUrl(mapId, edgeBasePath);
    const trendConfig = TREND_CONFIG[trend] || TREND_CONFIG.neutral;
    const TrendIcon = trendConfig.Icon;
    const ratingColor =
        Number.isFinite(avgRating) && avgRating < 1
            ? colors.state.danger.main
            : colors.primary.main;

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: "0.65rem",
                width: "100%",
                px: { xs: "0.85rem", md: "1rem" },
                py: "0.85rem",
                borderRadius: "12px",
                minWidth: 0,
                boxSizing: "border-box",
                ...ROW_SURFACE_SX,
            }}
        >
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: {
                        xs: "auto 1fr",
                        lg: "5rem minmax(6rem, 1fr) minmax(8rem, 1.4fr) 5.25rem",
                    },
                    gap: { xs: "0.75rem", lg: "1.1rem" },
                    alignItems: "center",
                    width: "100%",
                    minWidth: 0,
                }}
            >
                <Box
                    sx={{
                        width: { xs: "4.5rem", md: "5rem" },
                        height: { xs: "3rem", md: "3.25rem" },
                        borderRadius: "10px",
                        overflow: "hidden",
                        flexShrink: 0,
                        bgcolor: colors.background.surface,
                        border: `1px solid ${colors.border.default}`,
                    }}
                >
                    {mapImageUrl ? (
                        <Box
                            component="img"
                            src={mapImageUrl}
                            alt={mapLabel}
                            sx={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                display: "block",
                            }}
                        />
                    ) : null}
                </Box>

                <Box
                    sx={{
                        gridColumn: { xs: 2, lg: 2 },
                        minWidth: 0,
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: "0.9rem",
                            fontWeight: 700,
                            color: colors.text.primary,
                            lineHeight: 1.2,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                        }}
                    >
                        {mapLabel}
                    </Typography>
                    <Typography
                        sx={{
                            mt: "0.2rem",
                            fontSize: "0.66rem",
                            fontWeight: 500,
                            color: "rgba(255,255,255,0.4)",
                            lineHeight: 1.2,
                            fontVariantNumeric: "tabular-nums",
                        }}
                    >
                        {gamesPlayed} {gamesPlayed === 1 ? "match" : "matches"}
                    </Typography>
                </Box>

                <Box
                    sx={{
                        gridColumn: { xs: "1 / -1", lg: 3 },
                        minWidth: 0,
                        width: "100%",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "baseline",
                            justifyContent: "space-between",
                            gap: "0.5rem",
                            mb: "0.35rem",
                        }}
                    >
                        <Typography sx={STAT_LABEL_SX}>Win Rate</Typography>
                        <Typography
                            sx={{
                                fontSize: "0.82rem",
                                fontWeight: 700,
                                color: colors.text.primary,
                                fontVariantNumeric: "tabular-nums",
                                lineHeight: 1,
                            }}
                        >
                            {winRate}%
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            height: "0.35rem",
                            borderRadius: "999px",
                            bgcolor: "rgba(255,255,255,0.06)",
                            overflow: "hidden",
                        }}
                    >
                        <Box
                            sx={{
                                width: `${Math.max(0, Math.min(100, winRate))}%`,
                                height: "100%",
                                borderRadius: "999px",
                                background: `linear-gradient(90deg, ${colors.primary.active} 0%, ${colors.accent.main} 100%)`,
                            }}
                        />
                    </Box>
                </Box>

                <Box
                    sx={{
                        gridColumn: { xs: "1 / -1", lg: 4 },
                        minWidth: 0,
                        textAlign: { xs: "left", lg: "right" },
                        justifySelf: { lg: "end" },
                    }}
                >
                    <Typography sx={STAT_LABEL_SX}>Avg Rating</Typography>
                    <Typography
                        sx={{
                            mt: "0.3rem",
                            fontSize: "0.95rem",
                            fontWeight: 800,
                            color: ratingColor,
                            lineHeight: 1,
                            fontVariantNumeric: "tabular-nums",
                        }}
                    >
                        {Number.isFinite(avgRating) ? avgRating.toFixed(2) : "—"}
                    </Typography>
                </Box>
            </Box>

            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.45rem",
                    minWidth: 0,
                    width: "100%",
                    mt: "0.35rem",
                    pt: "0.55rem",
                    borderTop: "1px solid rgba(255,255,255,0.05)",
                }}
            >
                <Typography sx={STAT_LABEL_SX}>Trend</Typography>
                <TrendIcon sx={{ fontSize: "0.9rem", color: trendConfig.color }} />
                <RecentResultsStrip results={recentResults} />
            </Box>
        </Box>
    );
}

function MapPerformanceListSkeleton({ count = 7 }) {
    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "0.65rem" }}>
            {Array.from({ length: count }).map((_, index) => (
                <Box
                    key={index}
                    sx={{
                        height: "4.75rem",
                        borderRadius: "12px",
                        bgcolor: "rgba(255,255,255,0.03)",
                        border: "1px solid rgba(255,255,255,0.04)",
                    }}
                />
            ))}
        </Box>
    );
}

MapPerformanceListSkeleton.propTypes = {
    count: PropTypes.number,
};

export default function MapPerformanceList({
    mapsStats = {},
    last25MatchData = {},
    isLoading = false,
    limit = null,
}) {
    const mapRatings = useMemo(
        () => buildMapRatingAverages(last25MatchData),
        [last25MatchData],
    );

    const maps = useMemo(
        () => buildMapRows(mapsStats, mapRatings, limit),
        [mapsStats, mapRatings, limit],
    );

    const skeletonCount = limit ?? 7;

    if (isLoading) {
        return <MapPerformanceListSkeleton count={skeletonCount} />;
    }

    if (maps.length === 0) {
        return (
            <EmptyDataState
                title="No map data"
                icon={MapRounded}
                minHeight="6rem"
                embedded
            >
                Matches on this filter will appear here
            </EmptyDataState>
        );
    }

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "0.65rem" }}>
            {maps.map((map) => (
                <MapPerformanceRow key={map.mapId} {...map} />
            ))}
        </Box>
    );
}

MapPerformanceList.propTypes = {
    mapsStats: PropTypes.object,
    last25MatchData: PropTypes.object,
    isLoading: PropTypes.bool,
    limit: PropTypes.number,
};
